// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import StatsSection from "@/UI/components/StatsSection";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import type { TyCouponRow } from "@/types";
import { formatDate, formatMoney, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; active: number; redeemed: number; discounted: number };

// --- Logic --------------------------------------------------------------------------------------

export default function CouponsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");

    const table = useResourceTable<TyCouponRow, TyStats>({
        path: "coupons",
        rowsKey: "coupons",
        query: { search, state: tab },
    });

    const isEditable = canEdit("coupons");

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.lists.coupons.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "active", label: t.lists.coupons.statActive, value: formatNumber(table.stats.active, lang), tone: "good" as const },
            { key: "redeemed", label: t.lists.coupons.statRedeemed, value: formatNumber(table.stats.redeemed, lang) },
            {
                key: "given",
                label: t.lists.coupons.statDiscounted,
                value: formatMoney(table.stats.discounted, lang, t.home.egp),
                tone: "warn" as const,
            },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TyCouponRow>[]>(
        () => [
            {
                key: "code",
                label: t.cols.code,
                width: "minmax(140px, 1fr)",
                render: (row) => (
                    <span className="list-cell-strong" dir="ltr">
                        {row.code}
                    </span>
                ),
                /* Unique, so single-row only on the server — an inline edit always is. */
                edit: isEditable ? { kind: "text", value: (row) => row.code } : undefined,
            },
            {
                key: "title",
                label: t.cols.title,
                width: "minmax(150px, 1.2fr)",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.title}</span>,
            },
            {
                key: "discount",
                label: t.cols.discount,
                width: "110px",
                render: (row) => (
                    <span>{row.discount.type === "percentage" ? `${row.discount.amount}%` : formatMoney(row.discount.amount, lang, t.home.egp)}</span>
                ),
            },
            {
                key: "isActive",
                label: t.cols.active,
                width: "90px",
                align: "center",
                render: (row) => <span>{row.isActive ? t.table.bulkOn : t.table.bulkOff}</span>,
                edit: isEditable ? { kind: "toggle", value: (row) => row.isActive } : undefined,
            },
            {
                key: "usedCount",
                label: t.cols.uses,
                width: "90px",
                align: "center",
                render: (row) => <span>{formatNumber(row.usedCount, lang)}</span>,
            },
            {
                key: "maxUses",
                label: t.cols.maxUses,
                width: "120px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.maxUses === null ? t.cols.unlimited : formatNumber(row.maxUses, lang)}</span>,
                edit: isEditable ? { kind: "number", nullable: true, value: (row) => row.maxUses } : undefined,
            },
            {
                key: "expiresAt",
                label: t.cols.expiry,
                width: "130px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.expiresAt ? formatDate(row.expiresAt, lang) : t.cols.never}</span>,
                edit: isEditable ? { kind: "date", nullable: true, value: (row) => row.expiresAt } : undefined,
            },
        ],
        [t, lang, isEditable]
    );

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-coupon");
        addComp({
            name: "confirm-delete-coupon",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.lists.coupons.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.lists.coupons.tabAll },
                    { value: "active", label: t.lists.coupons.tabActive },
                    { value: "inactive", label: t.lists.coupons.tabInactive },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.lists.coupons.search }}
            />

            <DataTable<TyCouponRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) =>
                    isEditable ? (
                        <button
                            type="button"
                            className="list-row-btn list-row-btn--danger"
                            onClick={() => confirmDelete([row._id], row.code)}
                            aria-label={t.table.deleteRow}
                        >
                            <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                        </button>
                    ) : null
                }
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[
                        { key: "isActive", label: t.cols.active, kind: "toggle" },
                        { key: "expiresAt", label: t.cols.expiry, kind: "date" },
                    ]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
